import { useEffect, useMemo, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { formatPhone } from '../../../shared/bingo.js';
import {
  fieldsIn,
  fillTemplate,
  measureSms,
  parseNumbers,
  parseRecipients,
  SMS_GAP_S,
  SMS_KEEP_DAYS,
  type SmsBatchView,
  type SmsGroupView,
  type SmsImport,
  type SmsMessageStatus,
  type SmsMessageView,
  type SmsRecipient,
  type SmsTemplateView,
} from '../../../shared/sms.js';
import type { BingoSmsConfig } from '../../../shared/bingo.js';

// The admin's SMS tab (user decisions 2026-10-01, sms-suunnitelma.md): the same as the desktop program
// AreenaSMS – a message to numbers and groups, a bulk send from a list, scheduling, the state of every
// message, recipient groups and message templates. The server sends (also when no page is open).

type Flash = { ok: boolean; text: string } | null;
interface Overview {
  batches: SmsBatchView[];
  templates: SmsTemplateView[];
  groups: SmsGroupView[];
  phone: BingoSmsConfig;
  queue: { sending: boolean; waiting: boolean; bingo: boolean };
}
type Out = { phone: string; name: string; text: string };

const SUBS = ['send', 'bulk', 'status', 'scheduled', 'lists'] as const;
type Sub = (typeof SUBS)[number];
const SUB_KEY = 'areena.smsTab';
const SUB_LABEL: Record<Sub, string> = { send: 'Lähetä', bulk: 'Joukkoviesti', status: 'Tila', scheduled: 'Ajastetut', lists: 'Ryhmät ja pohjat' };
const STATUS: Record<string, string> = {
  scheduled: 'ajastettu',
  missed: 'myöhästynyt',
  pending: 'odottaa',
  sending: 'lähetetään',
  paused: 'tauolla',
  sent: 'lähetetty',
  delivered: 'perillä',
  failed: 'epäonnistui',
  cancelled: 'peruttu',
  done: 'valmis',
};
const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const when = (ms: number | null) => (ms === null ? '' : new Date(ms).toLocaleString('fi-FI', { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }));
const plural = (n: number) => (n === 1 ? '1 viesti' : `${n} viestiä`);
const badge = (status: string) => <span class={`badge sms-${status}`}>{STATUS[status] ?? status}</span>;

function storedSub(): Sub {
  try {
    const v = localStorage.getItem(SUB_KEY);
    return (SUBS as readonly string[]).includes(v ?? '') ? (v as Sub) : 'send';
  } catch {
    return 'send';
  }
}

/** "112 / 160 merkkiä · 1 tekstiviesti", with the character that forces the 70 limit. */
function counter(text: string): string {
  const m = measureSms(text);
  const parts = m.parts === 1 ? '1 tekstiviesti' : `${m.parts} tekstiviestiä`;
  const note = m.unicode ? ` · merkki „${m.special.join(' ')}“ pudottaa rajan 70:een` : '';
  return `${m.length} / ${Math.max(1, m.parts) * m.perPart} merkkiä · ${parts}${note}`;
}

export function SmsAdmin() {
  const [view, setView] = useState<Overview | null>(null);
  const [flash, setFlash] = useState<Flash>(null);
  const [sub, setSubState] = useState<Sub>(storedSub());
  const setSub = (s: Sub) => {
    setSubState(s);
    try {
      localStorage.setItem(SUB_KEY, s);
    } catch {
      // not remembered
    }
  };
  const load = () =>
    api<Overview>('GET', '/api/admin/sms')
      .then(setView)
      .catch((err) => setFlash({ ok: false, text: errorText(err) }));
  useEffect(() => {
    void load();
  }, []);
  // Follow the sending; slower when nothing runs.
  useEffect(() => {
    const busy = view?.batches.some((b) => b.status === 'sending' || (b.counts.sent ?? 0) > 0);
    const timer = setTimeout(() => void load(), busy ? 3000 : 20_000);
    return () => clearTimeout(timer);
  }, [view]);
  if (!view) return <p class="muted">{t.admin.autoBingo.loading}</p>;
  const waiting = view.batches.filter((b) => b.status === 'scheduled' || b.status === 'missed').length;
  const ready = view.phone.url !== '' && view.phone.hasPassword;
  return (
    <div class="stack">
      {flash && (
        <p class={flash.ok ? 'success' : 'error'} role="status">
          {flash.text}
        </p>
      )}
      <p class={ready ? 'hint' : 'error'}>
        {ready ? `📱 Puhelin: ${view.phone.url} · viestit lähtevät ${SMS_GAP_S} s välein` : '📱 Puhelimen osoite ja tunnukset puuttuvat.'} (Puhelimen asetukset ja testiviesti: Bingo → Koodien jakelu → Puhelin.)
        {view.queue.waiting && ' ⚠ Puhelin ei vastaa – lähetys odottaa ja yrittää minuutin välein.'}
        {view.queue.bingo && ' ⏳ Bingon koodeja lähetetään – SMS-lähetys jatkuu sen jälkeen.'}
      </p>
      <div class="tabs bingo-subtabs" role="tablist">
        {SUBS.map((id) => (
          <button role="tab" key={id} aria-selected={sub === id} class={`tab ${sub === id ? 'active' : ''}`} onClick={() => setSub(id)}>
            {SUB_LABEL[id]}
            {id === 'scheduled' && waiting > 0 ? ` (${waiting})` : ''}
          </button>
        ))}
      </div>
      {sub === 'send' && <SendTab view={view} onView={setView} onFlash={setFlash} />}
      {sub === 'bulk' && <BulkTab view={view} onView={setView} onFlash={setFlash} />}
      {sub === 'status' && <StatusTab view={view} onView={setView} onFlash={setFlash} />}
      {sub === 'scheduled' && <ScheduledTab view={view} onView={setView} onFlash={setFlash} />}
      {sub === 'lists' && <ListsTab view={view} onView={setView} onFlash={setFlash} />}
    </div>
  );
}

interface TabProps {
  view: Overview;
  onView: (v: Overview) => void;
  onFlash: (f: Flash) => void;
}

// --- Schedule and sending, shared by the send tabs --------------------------------------------------

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function useSchedule() {
  const [on, setOn] = useState(false);
  const [day, setDay] = useState(today());
  const [time, setTime] = useState('18:00');
  const at = on ? new Date(`${day}T${time}`).getTime() : null;
  const ui = (
    <div class="sms-schedule">
      <label class="switch">
        <input type="checkbox" checked={on} onChange={(e) => setOn(e.currentTarget.checked)} />
        <span class="switch-track" aria-hidden="true" />
        Ajasta lähetys
      </label>
      <input type="date" value={day} disabled={!on} onInput={(e) => setDay(e.currentTarget.value)} aria-label="Päivä" />
      <input type="time" value={time} disabled={!on} onInput={(e) => setTime(e.currentTarget.value)} aria-label="Kellonaika" />
    </div>
  );
  return { on, at, ui, reset: () => setOn(false) };
}

/** Confirm, then send or schedule. */
function SendConfirm(props: { title: string; messages: Out[]; at: number | null; onDone: (v: Overview) => void; onCancel: () => void; onFlash: (f: Flash) => void }) {
  const parts = props.messages.reduce((s, m) => s + Math.max(1, measureSms(m.text).parts), 0);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      props.onDone(await api<Overview>('POST', '/api/admin/sms/send', { title: props.title, at: props.at, messages: props.messages }));
      props.onFlash({ ok: true, text: props.at ? `Ajastettu: ${when(props.at)} (${plural(props.messages.length)}).` : `Lähetys alkoi: ${plural(props.messages.length)} – seuraa Tila-välilehdellä.` });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
      props.onCancel();
    }
  };
  return (
    <ConfirmDialog
      title={props.at ? `Ajastetaanko ${plural(props.messages.length)}?` : `Lähetetäänkö ${plural(props.messages.length)}?`}
      confirmLabel={props.at ? '📅 Ajasta' : '📱 Lähetä'}
      cancelLabel={t.admin.cancel}
      confirmDisabled={busy}
      onConfirm={() => void go()}
      onCancel={props.onCancel}
    >
      <p>
        {plural(props.messages.length)}, yhteensä {parts} tekstiviestiosaa, lähtee puhelimesta {SMS_GAP_S} s välein
        {props.at ? ` alkaen ${when(props.at)}.` : '.'}
      </p>
      <p class="hint">Ensimmäinen: {props.messages[0]?.text}</p>
    </ConfirmDialog>
  );
}

function checkSend(messages: Out[], at: number | null, scheduleOn: boolean): string | null {
  if (messages.length === 0) return 'Lisää vähintään yksi vastaanottaja.';
  if (messages.some((m) => m.text.length === 0)) return 'Kirjoita viesti.';
  if (scheduleOn && (at === null || Number.isNaN(at) || at < Date.now() + 60_000)) return 'Valitse ajastukselle tuleva päivä ja kellonaika.';
  return null;
}

// --- Lähetä -------------------------------------------------------------------------------------------

function SendTab(props: TabProps) {
  const [numbers, setNumbers] = useState('');
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [pick, setPick] = useState('');
  const [body, setBody] = useState('');
  const [confirm, setConfirm] = useState<Out[] | null>(null);
  const schedule = useSchedule();
  const typed = parseNumbers(numbers);
  const recipients: SmsRecipient[] = [...typed.recipients];
  for (const g of props.view.groups.filter((x) => groupIds.includes(x.id)))
    for (const m of g.members)
      if (!recipients.some((r) => r.phone === m.phone)) recipients.push({ phone: m.phone, name: m.name, fields: { nimi: m.name, numero: formatPhone(m.phone) } });
  const messages: Out[] = recipients.map((r) => ({ phone: r.phone, name: r.name, text: fillTemplate(body, r.fields).trim() }));
  const groups = props.view.groups.filter((x) => groupIds.includes(x.id));
  const title = groups.length > 0 ? groups.map((g) => g.name).join(', ') : recipients.length === 1 ? formatPhone(recipients[0]!.phone) : 'Viesti';
  const send = () => {
    const error = checkSend(messages, schedule.at, schedule.on);
    if (error) return props.onFlash({ ok: false, text: error });
    if (/\{nimi\}/i.test(body) && recipients.some((r) => !r.name) && !confirm_(`Viestissä on {nimi}, mutta osalla vastaanottajista ei ole nimeä – heidän viestissään kohta jää tyhjäksi. Jatketaanko?`)) return;
    setConfirm(messages);
  };
  const saveTemplate = async () => {
    if (!body.trim()) return props.onFlash({ ok: false, text: 'Kirjoita ensin viesti.' });
    const name = window.prompt('Viestipohjan nimi')?.trim();
    if (!name) return;
    const existing = props.view.templates.find((x) => x.name.toLowerCase() === name.toLowerCase());
    if (existing && !confirm_(`Pohja "${existing.name}" on jo olemassa. Korvataanko?`)) return;
    try {
      props.onView(await api<Overview>('POST', '/api/admin/sms/templates', { id: existing?.id ?? null, name, text: body }));
      props.onFlash({ ok: true, text: `Pohja "${name}" tallennettu.` });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  return (
    <section class="card form">
      <h2>Uusi viesti</h2>
      <label>
        Vastaanottajat (numerot pilkulla tai riveittäin)
        <textarea rows={2} value={numbers} onInput={(e) => setNumbers(e.currentTarget.value)} placeholder="040 123 4567, 050 765 4321" />
      </label>
      {groups.length > 0 && (
        <div class="sms-chips">
          {groups.map((g) => (
            <span class="sms-chip" key={g.id}>
              👥 {g.name} ({g.members.length})
              <button type="button" aria-label="Poista ryhmä" onClick={() => setGroupIds(groupIds.filter((x) => x !== g.id))}>
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <div class="sms-row">
        <select value={pick} onChange={(e) => setPick(e.currentTarget.value)}>
          <option value="">– valitse ryhmä –</option>
          {props.view.groups.map((g) => (
            <option key={g.id} value={String(g.id)}>
              {g.name} ({g.members.length})
            </option>
          ))}
        </select>
        <button class="btn btn-small" type="button" disabled={!pick} onClick={() => (setGroupIds([...new Set([...groupIds, Number(pick)])]), setPick(''))}>
          ＋ Lisää ryhmä
        </button>
      </div>
      <p class="hint">
        {recipients.length} vastaanottajaa{typed.rejected.length > 0 && ` · ei kelpaa: ${typed.rejected.map((r) => r.text).join(', ')}`}
      </p>
      <TemplatePicker templates={props.view.templates} body={body} onBody={setBody} />
      <label>
        Viesti
        <textarea rows={5} value={body} onInput={(e) => setBody(e.currentTarget.value)} />
      </label>
      <p class="hint sms-counter">
        <span>{messages[0] && body ? `Esikatselu: ${messages[0].text}` : 'Kentät: {nimi} = ryhmän jäsenen nimi, {numero} = numero.'}</span>
        <span>{counter(messages[0]?.text ?? body)}</span>
      </p>
      {schedule.ui}
      <div class="bingo-start-actions">
        <button class="btn btn-primary" type="button" onClick={send}>
          {schedule.on ? `📅 Ajasta ${plural(recipients.length)}` : `📱 Lähetä ${plural(recipients.length)}`}
        </button>
        <button class="btn btn-small" type="button" onClick={() => void saveTemplate()}>
          Tallenna pohjaksi…
        </button>
        <button class="btn btn-small" type="button" onClick={() => (setNumbers(''), setBody(''), setGroupIds([]), schedule.reset())}>
          Tyhjennä
        </button>
      </div>
      {confirm && (
        <SendConfirm
          title={title}
          messages={confirm}
          at={schedule.at}
          onFlash={props.onFlash}
          onCancel={() => setConfirm(null)}
          onDone={(v) => {
            props.onView(v);
            setConfirm(null);
            setNumbers('');
            setBody('');
            setGroupIds([]);
            schedule.reset();
          }}
        />
      )}
    </section>
  );
}

const confirm_ = (text: string) => window.confirm(text);

function TemplatePicker(props: { templates: SmsTemplateView[]; body: string; onBody: (b: string) => void }) {
  return (
    <label>
      Viestipohja
      <select
        value=""
        onChange={(e) => {
          const tpl = props.templates.find((x) => String(x.id) === e.currentTarget.value);
          if (tpl && (!props.body || props.body === tpl.text || confirm_(`Korvataanko viesti pohjalla "${tpl.name}"?`))) props.onBody(tpl.text);
          e.currentTarget.value = '';
        }}
      >
        <option value="">{props.templates.length ? '– valitse pohja –' : '– ei tallennettuja pohjia –'}</option>
        {props.templates.map((x) => (
          <option key={x.id} value={String(x.id)}>
            {x.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// --- Joukkoviesti -------------------------------------------------------------------------------------

/** A CSV from Excel is UTF-8 or the Windows code page: both are read right. */
async function readFile(file: File): Promise<string> {
  const bytes = await file.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes).replace(/^﻿/, '');
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

function BulkTab(props: TabProps) {
  const [list, setList] = useState<SmsImport>({ recipients: [], rejected: [], columns: [] });
  const [paste, setPaste] = useState('');
  const [body, setBody] = useState('');
  const [title, setTitle] = useState('Joukkoviesti');
  const [confirm, setConfirm] = useState<Out[] | null>(null);
  const schedule = useSchedule();
  const messages: Out[] = useMemo(() => list.recipients.map((r) => ({ phone: r.phone, name: r.name, text: fillTemplate(body, r.fields).trim() })), [list, body]);
  const columns = list.columns.filter(Boolean);
  const known = new Set([...columns.map((c) => c.toLowerCase()), 'nimi', 'numero']);
  const unknown = fieldsIn(body).filter((f) => !known.has(f.toLowerCase()));
  const longest = messages.reduce((a, m) => (measureSms(m.text).length > measureSms(a).length ? m.text : a), body);
  const takeGroup = (id: string) => {
    const g = props.view.groups.find((x) => String(x.id) === id);
    if (!g) return;
    setList(parseRecipients(['nimi\tnumero', ...g.members.map((m) => `${m.name.replace(/\t/g, ' ')}\t${m.phone}`)].join('\n')));
    setTitle(g.name);
  };
  const saveGroup = async () => {
    if (list.recipients.length === 0) return props.onFlash({ ok: false, text: 'Lista on tyhjä.' });
    const name = window.prompt('Ryhmän nimi', title === 'Joukkoviesti' ? '' : title)?.trim();
    if (!name) return;
    const existing = props.view.groups.find((x) => x.name.toLowerCase() === name.toLowerCase());
    if (existing && !confirm_(`Ryhmä "${existing.name}" on jo olemassa. Korvataanko sen jäsenet?`)) return;
    try {
      props.onView(await api<Overview>('POST', '/api/admin/sms/groups', { id: existing?.id ?? null, name, members: list.recipients.map((r) => ({ name: r.name.slice(0, 60), phone: r.phone })) }));
      props.onFlash({ ok: true, text: `Ryhmä "${name}" tallennettu (${list.recipients.length} jäsentä).` });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  const send = () => {
    const error = checkSend(messages, schedule.at, schedule.on);
    if (error) return props.onFlash({ ok: false, text: error });
    if (unknown.length > 0 && !confirm_(`Viestissä on kenttiä, joita listassa ei ole: ${unknown.map((u) => `{${u}}`).join(', ')}. Ne jäävät viestiin sellaisinaan. Jatketaanko?`)) return;
    setConfirm(messages);
  };
  return (
    <div class="sms-two">
      <section class="card form">
        <h2>Vastaanottajat</h2>
        <p class="hint">
          Tuo CSV-tiedosto tai kopioi rivit Excelistä ja liitä alle. Otsikkorivi (esim. Nimi, Puhelin, Pöytä) tekee sarakkeista viestin kenttiä: {'{nimi}'}, {'{pöytä}'} … Sama numero
          otetaan vain kerran.
        </p>
        <div class="sms-row">
          <label class="btn btn-small sms-file">
            📂 Avaa CSV-tiedosto…
            <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={async (e) => e.currentTarget.files?.[0] && setList(parseRecipients(await readFile(e.currentTarget.files[0])))} />
          </label>
          <select value="" onChange={(e) => takeGroup(e.currentTarget.value)}>
            <option value="">– ota tallennettu ryhmä –</option>
            {props.view.groups.map((g) => (
              <option key={g.id} value={String(g.id)}>
                {g.name} ({g.members.length})
              </option>
            ))}
          </select>
        </div>
        <label>
          Liitä Excelistä
          <textarea rows={3} value={paste} onInput={(e) => setPaste(e.currentTarget.value)} placeholder={'Nimi\tPuhelin\nMaija\t040 123 4567'} />
        </label>
        <div class="bingo-start-actions">
          <button class="btn btn-small" type="button" disabled={!paste.trim()} onClick={() => (setList(parseRecipients(paste)), setPaste(''))}>
            Lue lista
          </button>
          <button class="btn btn-small" type="button" onClick={() => setList({ recipients: [], rejected: [], columns: [] })}>
            Tyhjennä lista
          </button>
          <button class="btn btn-small" type="button" disabled={list.recipients.length === 0} onClick={() => void saveGroup()}>
            Tallenna ryhmäksi…
          </button>
        </div>
        <p class="hint">
          {list.recipients.length} vastaanottajaa{list.rejected.length > 0 && ` · ${list.rejected.length} riviä hylätty`}
        </p>
        {list.recipients.length > 0 && (
          <div class="sms-table-wrap sms-scroll">
            <table class="sms-table">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.recipients.slice(0, 300).map((r) => (
                  <tr key={r.phone}>
                    {columns.map((c) => (
                      <td key={c}>{r.fields[c]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list.rejected.length > 0 && (
          <details>
            <summary>Hylätyt rivit ({list.rejected.length})</summary>
            <ul class="sms-rejected">
              {list.rejected.map((r) => (
                <li key={r.line}>
                  Rivi {r.line}: {r.text} – {r.reason}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>
      <section class="card form">
        <h2>Viesti</h2>
        <TemplatePicker templates={props.view.templates} body={body} onBody={setBody} />
        <label>
          Teksti
          <textarea rows={6} value={body} onInput={(e) => setBody(e.currentTarget.value)} />
        </label>
        <p class="hint">
          Kentät: {(columns.length ? columns : ['nimi', 'numero']).map((c) => `{${c}}`).join(' ')}
          {unknown.length > 0 && <b class="sms-warn"> ⚠ tuntematon: {unknown.map((u) => `{${u}}`).join(', ')}</b>}
        </p>
        <p class="hint">Pisin viesti: {counter(longest)}</p>
        <div class="sms-preview">{messages[0]?.text || body || '–'}</div>
        {schedule.ui}
        <label>
          Lähetyksen nimi (näkyy Tila- ja Ajastetut-välilehdillä)
          <input value={title} maxLength={80} onInput={(e) => setTitle(e.currentTarget.value)} />
        </label>
        <div class="bingo-start-actions">
          <button class="btn btn-primary" type="button" onClick={send}>
            {schedule.on ? `📅 Ajasta ${plural(messages.length)}` : `📱 Lähetä ${plural(messages.length)}`}
          </button>
        </div>
      </section>
      {confirm && (
        <SendConfirm
          title={title.trim() || 'Joukkoviesti'}
          messages={confirm}
          at={schedule.at}
          onFlash={props.onFlash}
          onCancel={() => setConfirm(null)}
          onDone={(v) => {
            props.onView(v);
            setConfirm(null);
            schedule.reset();
          }}
        />
      )}
    </div>
  );
}

// --- Tila ---------------------------------------------------------------------------------------------

function StatusTab(props: TabProps) {
  const batches = props.view.batches.filter((b) => b.status !== 'scheduled' && b.status !== 'missed');
  const [selected, setSelected] = useState<number | null>(batches[0]?.id ?? null);
  const [messages, setMessages] = useState<SmsMessageView[]>([]);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [ask, setAsk] = useState<'cancel' | 'clear' | null>(null);
  const batch = batches.find((b) => b.id === selected) ?? null;
  useEffect(() => {
    if (selected === null) return setMessages([]);
    api<{ messages: SmsMessageView[] }>('GET', `/api/admin/sms/batches/${selected}`)
      .then((r) => setMessages(r.messages))
      .catch(() => setMessages([]));
  }, [selected, props.view]);
  const run = async (method: 'POST', path: string, body?: unknown, done?: string) => {
    try {
      props.onView(await api<Overview>(method, path, body));
      if (done) props.onFlash({ ok: true, text: done });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  const failed = messages.filter((m) => m.status === 'failed');
  const retryIds = [...checked].filter((id) => failed.some((m) => m.id === id));
  const exportCsv = () => {
    if (!batch) return;
    const q = (s: string | null) => `"${(s ?? '').replace(/"/g, '""')}"`;
    const rows = messages.map((m) => [q(m.name), q(formatPhone(m.phone)), q(STATUS[m.status] ?? m.status), q(m.sentAt ? new Date(m.sentAt).toLocaleString('fi-FI') : ''), q(m.text), q(m.error)].join(';'));
    const blob = new Blob(['﻿' + ['Nimi;Numero;Tila;Lähetetty;Viesti;Virhe', ...rows].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${batch.title} ${new Date(batch.createdAt).toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const c = (s: SmsMessageStatus) => batch?.counts[s] ?? 0;
  return (
    <div class="sms-two sms-two-status">
      <section class="card form">
        <h2>Lähetykset</h2>
        <p class="hint">Valmiit lähetykset poistuvat automaattisesti {SMS_KEEP_DAYS} päivän kuluttua.</p>
        {batches.length === 0 ? (
          <p class="muted">Ei lähetyksiä.</p>
        ) : (
          <ul class="sms-batches">
            {batches.map((b) => (
              <li key={b.id}>
                <button type="button" class={b.id === selected ? 'active' : ''} onClick={() => (setSelected(b.id), setChecked(new Set()))}>
                  <span>
                    <b>{b.title}</b>
                    <small>
                      {when(b.scheduledAt ?? b.createdAt)} · {plural(b.total)} · perillä {b.counts.delivered ?? 0}
                      {(b.counts.failed ?? 0) > 0 && ` · epäonnistui ${b.counts.failed}`}
                    </small>
                  </span>
                  {badge(b.status)}
                </button>
              </li>
            ))}
          </ul>
        )}
        <button class="btn btn-danger btn-small" type="button" disabled={batches.every((b) => b.status !== 'done' && b.status !== 'cancelled')} onClick={() => setAsk('clear')}>
          Tyhjennä historia
        </button>
      </section>
      <section class="card form">
        <h2>{batch?.title ?? 'Viestit'}</h2>
        {batch && (
          <>
            <p class="hint">
              Perillä {c('delivered')} · lähetetty {c('sent')} · odottaa {c('pending') + c('sending')} · epäonnistui {c('failed')}
              {c('cancelled') > 0 && ` · peruttu ${c('cancelled')}`}
              {batch.createdBy && ` · lähettäjä ${batch.createdBy}`}
            </p>
            <div class="bingo-start-actions">
              {batch.status === 'sending' && (
                <button class="btn btn-small" type="button" onClick={() => void run('POST', `/api/admin/sms/batches/${batch.id}/pause`)}>
                  ⏸ Tauko
                </button>
              )}
              {batch.status === 'paused' && (
                <button class="btn btn-small" type="button" onClick={() => void run('POST', `/api/admin/sms/batches/${batch.id}/resume`)}>
                  ▶ Jatka
                </button>
              )}
              {(batch.status === 'sending' || batch.status === 'paused') && (
                <button class="btn btn-danger btn-small" type="button" onClick={() => setAsk('cancel')}>
                  Peru lähettämättömät
                </button>
              )}
              <button
                class="btn btn-small"
                type="button"
                disabled={failed.length === 0}
                onClick={() => void run('POST', '/api/admin/sms/retry', { ids: retryIds.length ? retryIds : failed.map((m) => m.id) }, 'Epäonnistuneet lähetetään uudelleen.')}
              >
                ↻ Lähetä {retryIds.length ? `valitut (${retryIds.length})` : `epäonnistuneet (${failed.length})`} uudelleen
              </button>
              <button class="btn btn-small" type="button" onClick={exportCsv}>
                Vie CSV:ksi
              </button>
            </div>
            <div class="sms-table-wrap sms-scroll">
              <table class="sms-table">
                <thead>
                  <tr>
                    <th />
                    <th>Vastaanottaja</th>
                    <th>Tila</th>
                    <th>Lähetetty</th>
                    <th>Viesti / virhe</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((m) => (
                    <tr key={m.id}>
                      <td>
                        {m.status === 'failed' && (
                          <input
                            type="checkbox"
                            checked={checked.has(m.id)}
                            aria-label="Valitse"
                            onChange={(e) => {
                              const next = new Set(checked);
                              if (e.currentTarget.checked) next.add(m.id);
                              else next.delete(m.id);
                              setChecked(next);
                            }}
                          />
                        )}
                      </td>
                      <td>
                        {m.name && <div>{m.name}</div>}
                        <div class="muted">{formatPhone(m.phone)}</div>
                      </td>
                      <td>{badge(m.status)}</td>
                      <td>{m.sentAt ? new Date(m.sentAt).toLocaleTimeString('fi-FI') : ''}</td>
                      <td>{m.error ? <span class="sms-error">{m.error}</span> : <span class="sms-text">{m.text}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
      {ask && (
        <ConfirmDialog
          title={ask === 'clear' ? 'Tyhjennetäänkö historia?' : `Perutaanko lähetyksen "${batch?.title}" lähettämättömät viestit?`}
          confirmLabel={ask === 'clear' ? 'Tyhjennä' : 'Peru'}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => {
            setAsk(null);
            if (ask === 'clear') void run('POST', '/api/admin/sms/clear', undefined, 'Historia tyhjennetty.');
            else if (batch) void run('POST', `/api/admin/sms/batches/${batch.id}/cancel`, undefined, 'Lähettämättömät peruttu.');
          }}
          onCancel={() => setAsk(null)}
        >
          <p>{ask === 'clear' ? 'Valmiit ja perutut lähetykset viesteineen poistetaan. Ajastetut ja kesken olevat jäävät.' : 'Jo lähetetyt viestit jäävät historiaan.'}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

// --- Ajastetut ----------------------------------------------------------------------------------------

function ScheduledTab(props: TabProps) {
  const list = props.view.batches.filter((b) => b.status === 'scheduled' || b.status === 'missed').sort((a, b) => (a.scheduledAt ?? 0) - (b.scheduledAt ?? 0));
  return (
    <section class="card form">
      <h2>Ajastetut lähetykset</h2>
      <p class="hint">
        Palvelin lähettää ajastetut viestit itse, vaikka mikään sivu ei olisi auki. Jos palvelin oli ajastushetkellä poissa käytöstä (esim. päivitys), lähetys merkitään myöhästyneeksi
        eikä lähde kysymättä.
      </p>
      {list.length === 0 ? <p class="muted">Ei ajastettuja lähetyksiä.</p> : list.map((b) => <ScheduledRow key={b.id} batch={b} {...props} />)}
    </section>
  );
}

function ScheduledRow(props: TabProps & { batch: SmsBatchView }) {
  const b = props.batch;
  const d = new Date(b.scheduledAt ?? Date.now());
  const [day, setDay] = useState(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  const [time, setTime] = useState(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
  const [ask, setAsk] = useState<'now' | 'cancel' | null>(null);
  const run = async (path: string, body?: unknown, done?: string) => {
    try {
      props.onView(await api<Overview>('POST', `/api/admin/sms/batches/${b.id}/${path}`, body));
      if (done) props.onFlash({ ok: true, text: done });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  const save = () => {
    const at = new Date(`${day}T${time}`).getTime();
    if (Number.isNaN(at) || at < Date.now() + 60_000) return props.onFlash({ ok: false, text: 'Anna tuleva päivä ja kellonaika.' });
    void run('reschedule', { at }, `Uusi aika: ${when(at)}.`);
  };
  return (
    <div class="sms-scheduled">
      <div class="sms-scheduled-head">
        <b class="sms-when">{when(b.scheduledAt)}</b>
        <span>
          <b>{b.title}</b> · {plural(b.total)}
        </span>
        {badge(b.status)}
      </div>
      {b.status === 'missed' && <p class="error">Myöhästynyt – ei lähtenyt, koska palvelin ei ollut käynnissä. Lähetä nyt, anna uusi aika tai peru.</p>}
      <p class="hint sms-text">{b.sample}</p>
      <div class="sms-row">
        <input type="date" value={day} onInput={(e) => setDay(e.currentTarget.value)} aria-label="Päivä" />
        <input type="time" value={time} onInput={(e) => setTime(e.currentTarget.value)} aria-label="Kellonaika" />
        <button class="btn btn-small" type="button" onClick={save}>
          Tallenna aika
        </button>
        <button class="btn btn-small btn-primary" type="button" onClick={() => setAsk('now')}>
          Lähetä heti
        </button>
        <button class="btn btn-small btn-danger" type="button" onClick={() => setAsk('cancel')}>
          Peru
        </button>
      </div>
      {ask && (
        <ConfirmDialog
          title={ask === 'now' ? `Lähetetäänkö "${b.title}" heti?` : `Perutaanko "${b.title}"?`}
          confirmLabel={ask === 'now' ? '📱 Lähetä' : 'Peru lähetys'}
          cancelLabel={t.admin.cancel}
          danger={ask === 'cancel'}
          onConfirm={() => {
            setAsk(null);
            void (ask === 'now' ? run('send-now', undefined, 'Lähetys alkoi.') : run('cancel', undefined, 'Ajastus peruttu.'));
          }}
          onCancel={() => setAsk(null)}
        >
          <p>{plural(b.total)}.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

// --- Ryhmät ja pohjat ---------------------------------------------------------------------------------

function ListsTab(props: TabProps) {
  return (
    <div class="sms-two">
      <GroupsCard {...props} />
      <TemplatesCard {...props} />
    </div>
  );
}

function GroupsCard(props: TabProps) {
  const [id, setId] = useState<number | null>(props.view.groups[0]?.id ?? null);
  const group = props.view.groups.find((g) => g.id === id) ?? null;
  const [name, setName] = useState(group?.name ?? '');
  const [rows, setRows] = useState(() => (group?.members ?? []).map((m) => ({ name: m.name, phone: formatPhone(m.phone) })));
  const [paste, setPaste] = useState('');
  const [ask, setAsk] = useState(false);
  const choose = (next: number | null) => {
    const g = props.view.groups.find((x) => x.id === next) ?? null;
    setId(next);
    setName(g?.name ?? '');
    setRows((g?.members ?? []).map((m) => ({ name: m.name, phone: formatPhone(m.phone) })));
  };
  const set = (i: number, key: 'name' | 'phone', value: string) => setRows(rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  const save = async () => {
    if (!name.trim()) return props.onFlash({ ok: false, text: 'Anna ryhmälle nimi.' });
    const filled = rows.filter((r) => r.name.trim() || r.phone.trim());
    const bad = filled.filter((r) => !parseNumbers(r.phone).recipients.length);
    if (bad.length > 0 && !confirm_(`Näissä riveissä numero ei kelpaa, ja ne jäävät pois:\n${bad.map((r) => `${r.name} ${r.phone}`.trim()).join('\n')}\n\nTallennetaanko muut?`)) return;
    const seen = new Set<string>();
    const members = filled
      .map((r) => ({ name: r.name.trim().slice(0, 60), phone: parseNumbers(r.phone).recipients[0]?.phone ?? '' }))
      .filter((m) => m.phone && !seen.has(m.phone) && seen.add(m.phone));
    try {
      const r = await api<Overview & { id: number }>('POST', '/api/admin/sms/groups', { id, name: name.trim(), members });
      props.onView(r);
      setId(r.id);
      setRows(members.map((m) => ({ name: m.name, phone: formatPhone(m.phone) })));
      props.onFlash({ ok: true, text: `Ryhmä "${name.trim()}" tallennettu (${members.length} jäsentä).` });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  const remove = async () => {
    setAsk(false);
    if (id === null) return;
    try {
      props.onView(await api<Overview>('DELETE', `/api/admin/sms/groups/${id}`));
      choose(null);
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  return (
    <section class="card form">
      <h2>Vastaanottajaryhmät</h2>
      <div class="sms-row">
        <select value={id === null ? '' : String(id)} onChange={(e) => choose(e.currentTarget.value ? Number(e.currentTarget.value) : null)}>
          <option value="">＋ uusi ryhmä</option>
          {props.view.groups.map((g) => (
            <option key={g.id} value={String(g.id)}>
              {g.name} ({g.members.length})
            </option>
          ))}
        </select>
        {id !== null && (
          <button class="btn btn-small btn-danger" type="button" onClick={() => setAsk(true)}>
            Poista ryhmä
          </button>
        )}
      </div>
      <label>
        Nimi
        <input value={name} maxLength={60} onInput={(e) => setName(e.currentTarget.value)} />
      </label>
      <div class="sms-table-wrap sms-scroll">
        <table class="sms-table sms-members">
          <thead>
            <tr>
              <th>Nimi</th>
              <th>Numero</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>
                  <input value={r.name} maxLength={60} onInput={(e) => set(i, 'name', e.currentTarget.value)} aria-label="Nimi" />
                </td>
                <td>
                  <input value={r.phone} onInput={(e) => set(i, 'phone', e.currentTarget.value)} aria-label="Numero" />
                </td>
                <td>
                  <button class="btn btn-small" type="button" aria-label="Poista rivi" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div class="bingo-start-actions">
        <button class="btn btn-small" type="button" onClick={() => setRows([...rows, { name: '', phone: '' }])}>
          ＋ Rivi
        </button>
      </div>
      <label>
        Lisää rivejä Excelistä (nimi ja numero)
        <textarea rows={2} value={paste} onInput={(e) => setPaste(e.currentTarget.value)} />
      </label>
      <div class="bingo-start-actions">
        <button
          class="btn btn-small"
          type="button"
          disabled={!paste.trim()}
          onClick={() => {
            const list = parseRecipients(paste);
            setRows([...rows, ...list.recipients.map((r) => ({ name: r.name, phone: formatPhone(r.phone) }))]);
            setPaste('');
            props.onFlash({ ok: true, text: `Lisätty ${list.recipients.length} riviä${list.rejected.length ? `, ${list.rejected.length} hylätty` : ''} – muista Tallenna ryhmä.` });
          }}
        >
          Lisää rivit
        </button>
        <button class="btn btn-primary" type="button" onClick={() => void save()}>
          Tallenna ryhmä
        </button>
      </div>
      {ask && (
        <ConfirmDialog title={`Poistetaanko ryhmä "${group?.name}"?`} confirmLabel="Poista" cancelLabel={t.admin.cancel} danger onConfirm={() => void remove()} onCancel={() => setAsk(false)}>
          <p>{group?.members.length ?? 0} jäsentä.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function TemplatesCard(props: TabProps) {
  const [id, setId] = useState<number | null>(props.view.templates[0]?.id ?? null);
  const tpl = props.view.templates.find((x) => x.id === id) ?? null;
  const [name, setName] = useState(tpl?.name ?? '');
  const [text, setText] = useState(tpl?.text ?? '');
  const choose = (next: number | null) => {
    const x = props.view.templates.find((y) => y.id === next) ?? null;
    setId(next);
    setName(x?.name ?? '');
    setText(x?.text ?? '');
  };
  const save = async () => {
    if (!name.trim() || !text.trim()) return props.onFlash({ ok: false, text: 'Anna pohjalle nimi ja teksti.' });
    try {
      const v = await api<Overview>('POST', '/api/admin/sms/templates', { id, name: name.trim(), text });
      props.onView(v);
      setId(v.templates.find((x) => x.name === name.trim())?.id ?? id);
      props.onFlash({ ok: true, text: `Pohja "${name.trim()}" tallennettu.` });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  const remove = async () => {
    if (id === null || !confirm_(`Poistetaanko pohja "${tpl?.name}"?`)) return;
    try {
      props.onView(await api<Overview>('DELETE', `/api/admin/sms/templates/${id}`));
      choose(null);
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  return (
    <section class="card form">
      <h2>Viestipohjat</h2>
      <div class="sms-row">
        <select value={id === null ? '' : String(id)} onChange={(e) => choose(e.currentTarget.value ? Number(e.currentTarget.value) : null)}>
          <option value="">＋ uusi pohja</option>
          {props.view.templates.map((x) => (
            <option key={x.id} value={String(x.id)}>
              {x.name}
            </option>
          ))}
        </select>
        {id !== null && (
          <button class="btn btn-small btn-danger" type="button" onClick={() => void remove()}>
            Poista pohja
          </button>
        )}
      </div>
      <label>
        Nimi
        <input value={name} maxLength={60} onInput={(e) => setName(e.currentTarget.value)} />
      </label>
      <label>
        Teksti
        <textarea rows={7} value={text} maxLength={1000} onInput={(e) => setText(e.currentTarget.value)} />
      </label>
      <p class="hint">{counter(text)}</p>
      <p class="hint">Kentät: {'{nimi}'} ja {'{numero}'}; joukkoviestissä myös listan sarakkeet, esim. {'{pöytä}'}.</p>
      <div class="bingo-start-actions">
        <button class="btn btn-primary" type="button" onClick={() => void save()}>
          Tallenna pohja
        </button>
      </div>
    </section>
  );
}
